import { useState, useEffect } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { 
  ArrowRight, ArrowLeft, CheckCircle, AlertTriangle, Info,
  Target, Users, Heart, Shield, BarChart3, FileText, Compass, ClipboardList
} from "lucide-react";
import { toast } from "sonner";
import CompassTest from "@/components/CompassTest";
import SchedaEpisodio from "@/components/SchedaEpisodio";

// SIB-25 Items Configuration
const SIB_ITEMS = [
  { id: 1, text: "Iniziare una conversazione con una persona non conosciuta precedentemente.", subscale: "iniziativa" },
  { id: 2, text: "Raccontare qualcosa che le è accaduto di fronte ad un gruppo di persone.", subscale: "iniziativa" },
  { id: 3, text: "Chiedere ad una persona di spiegarle qualcosa che non ha capito.", subscale: "limiti" },
  { id: 4, text: "Accettare un complimento a proposito del suo aspetto fisico.", subscale: "positiva" },
  { id: 5, text: "Dire ad una persona che le è molto simpatica.", subscale: "iniziativa" },
  { id: 6, text: "Rifiutare una richiesta che proviene da una persona che ha autorità.", subscale: "negativa" },
  { id: 7, text: "Dire ad una persona che Lei le vuole bene.", subscale: "positiva" },
  { id: 8, text: "Rifiutarsi di prestare qualcosa ad uno/a stretto/a conoscente.", subscale: "limiti" },
  { id: 9, text: "Dire ad una persona che ritiene di essere stato/a trattato/a in modo ingiusto.", subscale: "negativa" },
  { id: 10, text: "Dire ad una persona che ha l'impressione di essere dalla stessa evitato/a.", subscale: "negativa" },
  { id: 11, text: "Ammettere di provare piacere quando una persona le dice che Lei è simpatico/a.", subscale: "positiva" },
  { id: 12, text: "Accettare un complimento per qualcosa che ha fatto.", subscale: "positiva" },
  { id: 13, text: "Ammettere chiaramente di conoscere poco un determinato argomento.", subscale: "positiva" },
  { id: 14, text: "Dire ad una persona che le ha rivolto giustamente delle critiche che ha ragione.", subscale: "limiti" },
  { id: 15, text: "Chiedere ad una persona di interrompere un comportamento che la infastidisce.", subscale: "negativa" },
  { id: 16, text: "Accettare un complimento a proposito di qualcosa che ha realizzato.", subscale: "positiva" },
  { id: 17, text: "Dire che le piacciono molto le persone che dimostrano esplicitamente affetto.", subscale: "positiva" },
  { id: 18, text: "Rifiutare cose o servizi che non la soddisfano (per esempio, in un negozio, ristorante).", subscale: "positiva" },
  { id: 19, text: "Chiedere scusa per eventuali errori commessi.", subscale: "negativa" },
  { id: 20, text: "Inserirsi in una conversazione di un gruppo di persone.", subscale: "iniziativa" },
  { id: 21, text: "Esprimere la sua opinione anche in presenza di persone autorevoli.", subscale: "limiti" },
  { id: 22, text: "Avvicinarsi ad una persona per fare la sua conoscenza.", subscale: "iniziativa" },
  { id: 23, text: "Chiedere informazioni a degli sconosciuti.", subscale: "iniziativa" },
  { id: 24, text: "Mantenere le proprie idee nei confronti di una persona che sostiene fortemente le proprie.", subscale: "negativa" },
  { id: 25, text: "Insistere affinché una persona faccia la sua parte in un lavoro di gruppo.", subscale: "limiti" }
];

const SUBSCALES = {
  iniziativa: { label: "Assertività di Iniziativa", icon: Target, color: "text-blue-600", bgColor: "bg-blue-50", description: "Capacità di prendere l'iniziativa nelle interazioni sociali" },
  negativa: { label: "Assertività Negativa", icon: Shield, color: "text-red-600", bgColor: "bg-red-50", description: "Capacità di dire no, esprimere disaccordo, porre limiti" },
  limiti: { label: "Limiti Personali", icon: Users, color: "text-purple-600", bgColor: "bg-purple-50", description: "Capacità di gestire i propri confini personali" },
  positiva: { label: "Assertività Positiva", icon: Heart, color: "text-green-600", bgColor: "bg-green-50", description: "Capacità di accettare complimenti ed esprimere affetto" }
};

const DISAGIO_SCALE = [
  { value: 1, label: "Nessun disagio" },
  { value: 2, label: "Poco disagio" },
  { value: 3, label: "Abbastanza disagio" },
  { value: 4, label: "Molto disagio" },
  { value: 5, label: "Disagio intenso" }
];

const PERFORMANCE_SCALE = [
  { value: 1, label: "Mai" },
  { value: 2, label: "Raramente" },
  { value: 3, label: "Qualche volta" },
  { value: 4, label: "Solitamente" },
  { value: 5, label: "Sempre" }
];

const NOTA_METODOLOGICA = `
La Scale for Interpersonal Behavior (SIB) è uno strumento di autovalutazione che misura il comportamento assertivo in diverse situazioni interpersonali.

VERSIONE UTILIZZATA
Questa è una versione ridotta a 25 item, derivata dalla versione originale di Arrindell et al. (1984), adattata per l'italiano.

DIMENSIONI VALUTATE
• Assertività di iniziativa: capacità di prendere l'iniziativa nelle interazioni sociali
• Assertività negativa: capacità di dire no, esprimere disaccordo, porre limiti
• Limiti personali: capacità di gestire i propri confini personali
• Assertività positiva: capacità di accettare complimenti ed esprimere affetto

MODALITÀ DI COMPILAZIONE
1. Prima compila TUTTI gli item sulla colonna "Disagio" (quanto ti mette a disagio quella situazione)
2. Solo dopo, compila TUTTI gli item sulla colonna "Performance" (quanto spesso ti comporti così)

INTERPRETAZIONE DEI RISULTATI
I risultati sono calcolati confrontando:
• Media Disagio e Media Performance per ogni sottoscala
• Delta (Δ) = Performance - Disagio
• Profilo incrociato delle due dimensioni

IMPORTANTE
Questo strumento è utilizzato a scopo esclusivamente didattico ed esplorativo.
NON ha finalità diagnostiche o cliniche.
NON esistono norme standardizzate per la popolazione italiana.
I risultati devono essere interpretati come spunti di riflessione personale.
`;

const AssertivitaPage = () => {
  // MAIN VIEW: hub, sib, compass
  const [activeTest, setActiveTest] = useState("hub");
  
  const [step, setStep] = useState("intro"); // intro, disclaimer, disagio, performance, results
  const [disclaimerAccepted, setDisclaimerAccepted] = useState(false);
  const [responses, setResponses] = useState({});
  const [results, setResults] = useState(null);
  const [currentItemIndex, setCurrentItemIndex] = useState(0);
  const [loading, setLoading] = useState(false);
  const [previousResults, setPreviousResults] = useState([]);

  useEffect(() => {
    fetchPreviousResults();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchPreviousResults = async () => {
    try {
      const res = await api.get('/sib/results');
      setPreviousResults(res.data || []);
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        if (process.env.NODE_ENV === 'development') console.log("No previous results");
      }
    }
  };

  const resetSibTest = () => {
    setStep("intro");
    setResponses({});
    setResults(null);
    setDisclaimerAccepted(false);
    setCurrentItemIndex(0);
  };

  const goBackToHub = () => {
    setActiveTest("hub");
    resetSibTest();
  };

  // ============ HUB - TEST SELECTION ============
  if (activeTest === "hub") {
    return (
      <div className="max-w-2xl mx-auto space-y-6" data-testid="assertivita-page">
        <div className="text-center space-y-4">
          <div className="w-20 h-20 bg-gradient-to-br from-violet-500 to-purple-600 rounded-full flex items-center justify-center mx-auto">
            <BarChart3 className="w-10 h-10 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-gray-800">Training Assertività</h1>
          <p className="text-gray-600">Scegli un test per misurare e allenare le tue competenze assertive</p>
        </div>

        {/* Test Cards */}
        <div className="space-y-4">
          {/* SIB-25 Card */}
          <Card 
            className="cursor-pointer hover:shadow-lg transition-all hover:-translate-y-1 border-2 hover:border-indigo-300"
            onClick={() => setActiveTest("sib")}
          >
            <CardContent className="p-6">
              <div className="flex items-start gap-4">
                <div className="w-14 h-14 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl flex items-center justify-center flex-shrink-0">
                  <BarChart3 className="w-7 h-7 text-white" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="font-bold text-lg">Test SIB-25</h3>
                    <Badge variant="outline" className="text-xs">25 item</Badge>
                  </div>
                  <p className="text-sm text-gray-600 mb-2">
                    Misura il tuo livello di assertività su 4 dimensioni: iniziativa, assertività negativa, limiti personali, assertività positiva.
                  </p>
                  <p className="text-xs text-gray-500">Valuta disagio e frequenza dei comportamenti</p>
                </div>
                <ArrowRight className="w-5 h-5 text-gray-400" />
              </div>
            </CardContent>
          </Card>

          {/* COMPASS Card */}
          <Card 
            className="cursor-pointer hover:shadow-lg transition-all hover:-translate-y-1 border-2 hover:border-teal-300"
            onClick={() => setActiveTest("compass")}
          >
            <CardContent className="p-6">
              <div className="flex items-start gap-4">
                <div className="w-14 h-14 bg-gradient-to-br from-teal-500 to-cyan-600 rounded-xl flex items-center justify-center flex-shrink-0">
                  <span className="text-2xl">🧭</span>
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="font-bold text-lg">Test COMPASS</h3>
                    <Badge variant="outline" className="text-xs">30 situazioni</Badge>
                  </div>
                  <p className="text-sm text-gray-600 mb-2">
                    Impara a riconoscere i comportamenti assertivi, anassertivi e aggressivi in situazioni reali.
                  </p>
                  <p className="text-xs text-gray-500">Feedback immediato dopo ogni risposta</p>
                </div>
                <ArrowRight className="w-5 h-5 text-gray-400" />
              </div>
            </CardContent>
          </Card>

          {/* Scheda Episodio Card */}
          <Card 
            className="cursor-pointer hover:shadow-lg transition-all hover:-translate-y-1 border-2 hover:border-orange-300"
            onClick={() => setActiveTest("scheda")}
          >
            <CardContent className="p-6">
              <div className="flex items-start gap-4">
                <div className="w-14 h-14 bg-gradient-to-br from-orange-500 to-amber-600 rounded-xl flex items-center justify-center flex-shrink-0">
                  <span className="text-2xl">📝</span>
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="font-bold text-lg">Scheda Episodio</h3>
                    <Badge variant="outline" className="text-xs">Diario</Badge>
                  </div>
                  <p className="text-sm text-gray-600 mb-2">
                    Analizza situazioni problematiche o conflitti per capire il tuo stile comunicativo.
                  </p>
                  <p className="text-xs text-gray-500">Compila entro 30-60 min dall'episodio</p>
                </div>
                <ArrowRight className="w-5 h-5 text-gray-400" />
              </div>
            </CardContent>
          </Card>
        </div>

        {previousResults.length > 0 && (
          <Card className="border-indigo-200 bg-indigo-50/50">
            <CardContent className="p-4">
              <p className="text-sm text-indigo-700">
                <strong>Hai completato {previousResults.length} test SIB-25.</strong> Puoi rifarli per vedere i tuoi progressi.
              </p>
            </CardContent>
          </Card>
        )}
      </div>
    );
  }

  // ============ COMPASS TEST ============
  if (activeTest === "compass") {
    return (
      <div className="max-w-2xl mx-auto">
        <CompassTest onBack={goBackToHub} />
      </div>
    );
  }

  // ============ SCHEDA EPISODIO ============
  if (activeTest === "scheda") {
    return (
      <div className="max-w-2xl mx-auto">
        <SchedaEpisodio onBack={goBackToHub} />
      </div>
    );
  }

  // ============ SIB-25 TEST ============

  const handleDisagioResponse = (itemId, value) => {
    setResponses(prev => ({
      ...prev,
      [`disagio_${itemId}`]: value
    }));
  };

  const handlePerformanceResponse = (itemId, value) => {
    setResponses(prev => ({
      ...prev,
      [`performance_${itemId}`]: value
    }));
  };

  const getDisagioProgress = () => {
    const answered = SIB_ITEMS.filter(item => responses[`disagio_${item.id}`] !== undefined).length;
    return Math.round((answered / SIB_ITEMS.length) * 100);
  };

  const getPerformanceProgress = () => {
    const answered = SIB_ITEMS.filter(item => responses[`performance_${item.id}`] !== undefined).length;
    return Math.round((answered / SIB_ITEMS.length) * 100);
  };

  const allDisagioCompleted = () => {
    return SIB_ITEMS.every(item => responses[`disagio_${item.id}`] !== undefined);
  };

  const allPerformanceCompleted = () => {
    return SIB_ITEMS.every(item => responses[`performance_${item.id}`] !== undefined);
  };

  const calculateResults = () => {
    // Calculate subscale averages
    const subscaleResults = {};
    
    Object.keys(SUBSCALES).forEach(subscale => {
      const items = SIB_ITEMS.filter(item => item.subscale === subscale);
      
      const disagioSum = items.reduce((sum, item) => sum + (responses[`disagio_${item.id}`] || 0), 0);
      const performanceSum = items.reduce((sum, item) => sum + (responses[`performance_${item.id}`] || 0), 0);
      
      const disagioAvg = disagioSum / items.length;
      const performanceAvg = performanceSum / items.length;
      const delta = performanceAvg - disagioAvg;
      
      // Determine fasce
      let fasciaDisagio = "basso";
      if (disagioAvg > 3.5) fasciaDisagio = "alto";
      else if (disagioAvg > 1.5) fasciaDisagio = "moderato";
      
      let fasciaPerformance = "bassa";
      if (performanceAvg > 4.5) fasciaPerformance = "buona";
      else if (performanceAvg > 2.5) fasciaPerformance = "intermedia";
      
      // Generate feedback
      let feedback = "";
      let status = "neutral";
      
      if (delta < -2) {
        feedback = "Area critica: il disagio è molto superiore alla frequenza del comportamento. Potrebbe essere utile lavorare su questa area.";
        status = "critical";
      } else if (delta < -1) {
        feedback = "Fatica interna: provi più disagio di quanto riesci ad agire. C'è margine di crescita.";
        status = "warning";
      } else if (Math.abs(delta) <= 1) {
        feedback = "Zona neutra: c'è equilibrio tra disagio e comportamento. Situazione bilanciata.";
        status = "neutral";
      } else if (delta > 1) {
        feedback = "Buona integrazione: riesci ad agire nonostante il disagio. Ottimo!";
        status = "good";
      } else {
        feedback = "Integrazione positiva: il comportamento è ben integrato con bassi livelli di disagio.";
        status = "good";
      }
      
      subscaleResults[subscale] = {
        disagioAvg: Math.round(disagioAvg * 10) / 10,
        performanceAvg: Math.round(performanceAvg * 10) / 10,
        delta: Math.round(delta * 10) / 10,
        fasciaDisagio,
        fasciaPerformance,
        feedback,
        status,
        itemCount: items.length
      };
    });
    
    // Calculate totals
    const allDisagio = SIB_ITEMS.map(item => responses[`disagio_${item.id}`] || 0);
    const allPerformance = SIB_ITEMS.map(item => responses[`performance_${item.id}`] || 0);
    
    const totalDisagioAvg = allDisagio.reduce((a, b) => a + b, 0) / allDisagio.length;
    const totalPerformanceAvg = allPerformance.reduce((a, b) => a + b, 0) / allPerformance.length;
    const totalDelta = totalPerformanceAvg - totalDisagioAvg;
    
    let totalFasciaDisagio = "basso";
    if (totalDisagioAvg > 3.5) totalFasciaDisagio = "alto";
    else if (totalDisagioAvg > 1.5) totalFasciaDisagio = "moderato";
    
    let totalFasciaPerformance = "bassa";
    if (totalPerformanceAvg > 4.5) totalFasciaPerformance = "buona";
    else if (totalPerformanceAvg > 2.5) totalFasciaPerformance = "intermedia";
    
    let totalFeedback = "";
    let totalStatus = "neutral";
    
    if (totalDelta < -2) {
      totalFeedback = "Profilo generale: Area critica. Potrebbe essere utile un percorso di training sull'assertività.";
      totalStatus = "critical";
    } else if (totalDelta < -1) {
      totalFeedback = "Profilo generale: Fatica interna. Ci sono margini di crescita nell'assertività.";
      totalStatus = "warning";
    } else if (Math.abs(totalDelta) <= 1) {
      totalFeedback = "Profilo generale: Equilibrato. Il tuo livello di assertività è nella norma.";
      totalStatus = "neutral";
    } else {
      totalFeedback = "Profilo generale: Buona integrazione. Hai un buon livello di assertività!";
      totalStatus = "good";
    }
    
    return {
      subscales: subscaleResults,
      total: {
        disagioAvg: Math.round(totalDisagioAvg * 10) / 10,
        performanceAvg: Math.round(totalPerformanceAvg * 10) / 10,
        delta: Math.round(totalDelta * 10) / 10,
        fasciaDisagio: totalFasciaDisagio,
        fasciaPerformance: totalFasciaPerformance,
        feedback: totalFeedback,
        status: totalStatus
      },
      responses: { ...responses },
      completedAt: new Date().toISOString()
    };
  };

  const handleSubmit = async () => {
    setLoading(true);
    const calculatedResults = calculateResults();
    
    try {
      await api.post('/sib/results', calculatedResults);
      setResults(calculatedResults);
      setStep("results");
      // Scroll automatico verso l'alto per vedere i risultati
      window.scrollTo({ top: 0, behavior: 'smooth' });
      toast.success("Test completato!");
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        if (process.env.NODE_ENV === 'development') console.error("Error saving results:", error);
      }
      // Still show results even if save fails
      setResults(calculatedResults);
      setStep("results");
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } finally {
      setLoading(false);
    }
  };

  const resetTest = () => {
    setStep("intro");
    setResponses({});
    setResults(null);
    setDisclaimerAccepted(false);
    setCurrentItemIndex(0);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "good": return "text-green-600 bg-green-50 border-green-200";
      case "warning": return "text-yellow-600 bg-yellow-50 border-yellow-200";
      case "critical": return "text-red-600 bg-red-50 border-red-200";
      default: return "text-blue-600 bg-blue-50 border-blue-200";
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "good": return <CheckCircle className="w-5 h-5 text-green-500" />;
      case "warning": return <AlertTriangle className="w-5 h-5 text-yellow-500" />;
      case "critical": return <AlertTriangle className="w-5 h-5 text-red-500" />;
      default: return <Info className="w-5 h-5 text-blue-500" />;
    }
  };

  // ============ RENDER INTRO ============
  if (step === "intro") {
    return (
      <div className="max-w-2xl mx-auto space-y-6" data-testid="assertivita-page">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={goBackToHub} className="rounded-full">
            <ArrowLeft className="w-4 h-4 mr-1" /> Indietro
          </Button>
        </div>
        
        <div className="text-center space-y-4">
          <div className="w-20 h-20 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-full flex items-center justify-center mx-auto">
            <BarChart3 className="w-10 h-10 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-gray-800">Test SIB-25</h1>
          <p className="text-gray-600">
            Scopri il tuo profilo assertivo attraverso la Scale for Interpersonal Behavior
          </p>
        </div>

        <Card>
          <CardContent className="p-6 space-y-4">
            <div className="flex items-start gap-3">
              <Target className="w-6 h-6 text-blue-500 flex-shrink-0 mt-1" />
              <div>
                <h3 className="font-semibold">Cosa misura?</h3>
                <p className="text-sm text-gray-600">4 dimensioni dell'assertività: iniziativa, assertività negativa, limiti personali, assertività positiva</p>
              </div>
            </div>
            
            <div className="flex items-start gap-3">
              <FileText className="w-6 h-6 text-purple-500 flex-shrink-0 mt-1" />
              <div>
                <h3 className="font-semibold">Come funziona?</h3>
                <p className="text-sm text-gray-600">25 situazioni da valutare su due dimensioni: quanto disagio provi e quanto spesso ti comporti così</p>
              </div>
            </div>
            
            <div className="flex items-start gap-3">
              <BarChart3 className="w-6 h-6 text-green-500 flex-shrink-0 mt-1" />
              <div>
                <h3 className="font-semibold">Cosa otterrai?</h3>
                <p className="text-sm text-gray-600">Un profilo dettagliato con feedback per ogni area e suggerimenti personalizzati</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Button 
          onClick={() => setStep("disclaimer")} 
          className="w-full rounded-xl bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700"
          size="lg"
        >
          Inizia il Test <ArrowRight className="w-5 h-5 ml-2" />
        </Button>
      </div>
    );
  }

  // ============ RENDER DISCLAIMER ============
  if (step === "disclaimer") {
    return (
      <div className="max-w-2xl mx-auto space-y-6">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-800">Nota Metodologica</h1>
          <p className="text-gray-600 mt-2">Leggi attentamente prima di procedere</p>
        </div>

        <Card>
          <CardContent className="p-6">
            <div className="prose prose-sm max-w-none">
              <pre className="whitespace-pre-wrap text-sm text-gray-700 bg-gray-50 p-4 rounded-xl font-sans">
                {NOTA_METODOLOGICA}
              </pre>
            </div>
          </CardContent>
        </Card>

        <Card className="border-amber-200 bg-amber-50">
          <CardContent className="p-4">
            <label className="flex items-start gap-3 cursor-pointer">
              <Checkbox 
                checked={disclaimerAccepted} 
                onCheckedChange={setDisclaimerAccepted}
                className="mt-1"
              />
              <span className="text-sm text-amber-800">
                Ho letto e compreso la nota metodologica. Comprendo che questo strumento è utilizzato 
                a scopo esclusivamente didattico ed esplorativo e NON ha finalità diagnostiche o cliniche.
              </span>
            </label>
          </CardContent>
        </Card>

        <div className="flex gap-3">
          <Button variant="outline" onClick={() => setStep("intro")} className="flex-1 rounded-xl">
            <ArrowLeft className="w-4 h-4 mr-2" /> Indietro
          </Button>
          <Button 
            onClick={() => setStep("disagio")} 
            disabled={!disclaimerAccepted}
            className="flex-1 rounded-xl bg-gradient-to-r from-indigo-600 to-purple-600"
          >
            Procedi <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </div>
    );
  }

  // ============ RENDER DISAGIO PHASE ============
  if (step === "disagio") {
    return (
      <div className="max-w-3xl mx-auto space-y-6">
        <div className="sticky top-0 bg-white/95 backdrop-blur-sm z-10 pb-4 -mx-4 px-4 pt-2">
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-lg font-bold text-gray-800">Fase 1: Disagio</h2>
            <Badge variant="outline">{getDisagioProgress()}% completato</Badge>
          </div>
          <Progress value={getDisagioProgress()} className="h-2" />
          <p className="text-sm text-gray-500 mt-2">
            Per ogni situazione, indica quanto disagio/tensione ti provoca (1 = nessuno, 5 = intenso)
          </p>
        </div>

        <div className="space-y-4">
          {SIB_ITEMS.map((item, index) => (
            <Card key={item.id} className={`transition-all ${responses[`disagio_${item.id}`] ? 'border-green-200 bg-green-50/30' : ''}`}>
              <CardContent className="p-4">
                <div className="flex gap-3">
                  <span className="text-sm font-bold text-gray-400 w-6">{item.id}.</span>
                  <div className="flex-1">
                    <p className="text-sm text-gray-800 mb-3">{item.text}</p>
                    <div className="flex flex-wrap gap-2">
                      {DISAGIO_SCALE.map(option => (
                        <button
                          key={option.value}
                          onClick={() => handleDisagioResponse(item.id, option.value)}
                          className={`px-3 py-2 rounded-lg text-xs font-medium transition-all ${
                            responses[`disagio_${item.id}`] === option.value
                              ? 'bg-indigo-600 text-white shadow-md'
                              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                          }`}
                        >
                          {option.value} - {option.label}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        <div className="sticky bottom-4 bg-white/95 backdrop-blur-sm p-4 rounded-xl shadow-lg border">
          <div className="flex gap-3">
            <Button variant="outline" onClick={() => setStep("disclaimer")} className="rounded-xl">
              <ArrowLeft className="w-4 h-4 mr-2" /> Indietro
            </Button>
            <Button 
              onClick={() => {
                setStep("performance");
                // Scroll automatico verso l'alto
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }} 
              disabled={!allDisagioCompleted()}
              className="flex-1 rounded-xl bg-gradient-to-r from-indigo-600 to-purple-600"
            >
              Vai alla Fase 2: Performance <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
          {!allDisagioCompleted() && (
            <p className="text-xs text-center text-gray-500 mt-2">
              Completa tutti i {SIB_ITEMS.length} item per procedere ({SIB_ITEMS.filter(item => responses[`disagio_${item.id}`]).length}/{SIB_ITEMS.length})
            </p>
          )}
        </div>
      </div>
    );
  }

  // ============ RENDER PERFORMANCE PHASE ============
  if (step === "performance") {
    return (
      <div className="max-w-3xl mx-auto space-y-6">
        <div className="sticky top-0 bg-white/95 backdrop-blur-sm z-10 pb-4 -mx-4 px-4 pt-2">
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-lg font-bold text-gray-800">Fase 2: Performance</h2>
            <Badge variant="outline">{getPerformanceProgress()}% completato</Badge>
          </div>
          <Progress value={getPerformanceProgress()} className="h-2" />
          <p className="text-sm text-gray-500 mt-2">
            Per ogni situazione, indica quanto spesso ti comporti così (1 = mai, 5 = sempre)
          </p>
        </div>

        <div className="space-y-4">
          {SIB_ITEMS.map((item, index) => (
            <Card key={item.id} className={`transition-all ${responses[`performance_${item.id}`] ? 'border-green-200 bg-green-50/30' : ''}`}>
              <CardContent className="p-4">
                <div className="flex gap-3">
                  <span className="text-sm font-bold text-gray-400 w-6">{item.id}.</span>
                  <div className="flex-1">
                    <p className="text-sm text-gray-800 mb-3">{item.text}</p>
                    <div className="flex flex-wrap gap-2">
                      {PERFORMANCE_SCALE.map(option => (
                        <button
                          key={option.value}
                          onClick={() => handlePerformanceResponse(item.id, option.value)}
                          className={`px-3 py-2 rounded-lg text-xs font-medium transition-all ${
                            responses[`performance_${item.id}`] === option.value
                              ? 'bg-purple-600 text-white shadow-md'
                              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                          }`}
                        >
                          {option.value} - {option.label}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        <div className="sticky bottom-4 bg-white/95 backdrop-blur-sm p-4 rounded-xl shadow-lg border">
          <div className="flex gap-3">
            <Button variant="outline" onClick={() => setStep("disagio")} className="rounded-xl">
              <ArrowLeft className="w-4 h-4 mr-2" /> Torna al Disagio
            </Button>
            <Button 
              onClick={handleSubmit} 
              disabled={!allPerformanceCompleted() || loading}
              className="flex-1 rounded-xl bg-gradient-to-r from-indigo-600 to-purple-600"
            >
              {loading ? "Calcolo in corso..." : "Calcola Risultati"} <BarChart3 className="w-4 h-4 ml-2" />
            </Button>
          </div>
          {!allPerformanceCompleted() && (
            <p className="text-xs text-center text-gray-500 mt-2">
              Completa tutti i {SIB_ITEMS.length} item per procedere ({SIB_ITEMS.filter(item => responses[`performance_${item.id}`]).length}/{SIB_ITEMS.length})
            </p>
          )}
        </div>
      </div>
    );
  }

  // ============ RENDER RESULTS ============
  if (step === "results" && results) {
    return (
      <div className="max-w-3xl mx-auto space-y-6">
        <div className="text-center space-y-2">
          <h1 className="text-2xl font-bold text-gray-800">I Tuoi Risultati</h1>
          <p className="text-gray-600">Ecco il tuo profilo assertivo basato sulle tue risposte</p>
        </div>

        {/* Total Score Card */}
        <Card className={`border-2 ${getStatusColor(results.total.status)}`}>
          <CardContent className="p-6">
            <div className="flex items-center gap-4 mb-4">
              {getStatusIcon(results.total.status)}
              <div>
                <h2 className="text-lg font-bold">Profilo Generale</h2>
                <p className="text-sm opacity-80">Assertività complessiva</p>
              </div>
            </div>
            
            <div className="grid grid-cols-3 gap-4 mb-4">
              <div className="text-center p-3 bg-white/50 rounded-xl">
                <p className="text-2xl font-bold">{results.total.disagioAvg}</p>
                <p className="text-xs opacity-70">Disagio medio</p>
                <Badge variant="outline" className="mt-1 text-[10px]">{results.total.fasciaDisagio}</Badge>
              </div>
              <div className="text-center p-3 bg-white/50 rounded-xl">
                <p className="text-2xl font-bold">{results.total.performanceAvg}</p>
                <p className="text-xs opacity-70">Performance media</p>
                <Badge variant="outline" className="mt-1 text-[10px]">{results.total.fasciaPerformance}</Badge>
              </div>
              <div className="text-center p-3 bg-white/50 rounded-xl">
                <p className={`text-2xl font-bold ${results.total.delta >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {results.total.delta > 0 ? '+' : ''}{results.total.delta}
                </p>
                <p className="text-xs opacity-70">Delta (Δ)</p>
                <Badge variant="outline" className="mt-1 text-[10px]">
                  {Math.abs(results.total.delta) <= 1 ? 'Zona neutra' : results.total.delta > 0 ? 'Positivo' : 'Negativo'}
                </Badge>
              </div>
            </div>
            
            <p className="text-sm">{results.total.feedback}</p>
          </CardContent>
        </Card>

        {/* Subscale Results */}
        <h3 className="font-semibold text-gray-700">Dettaglio per Area</h3>
        
        <div className="grid gap-4">
          {Object.entries(results.subscales).map(([key, data]) => {
            const subscaleInfo = SUBSCALES[key];
            const Icon = subscaleInfo.icon;
            
            return (
              <Card key={key} className={`border ${getStatusColor(data.status)}`}>
                <CardContent className="p-4">
                  <div className="flex items-center gap-3 mb-3">
                    <div className={`w-10 h-10 rounded-full ${subscaleInfo.bgColor} flex items-center justify-center`}>
                      <Icon className={`w-5 h-5 ${subscaleInfo.color}`} />
                    </div>
                    <div className="flex-1">
                      <h4 className="font-semibold">{subscaleInfo.label}</h4>
                      <p className="text-xs text-gray-500">{subscaleInfo.description}</p>
                    </div>
                    {getStatusIcon(data.status)}
                  </div>
                  
                  <div className="grid grid-cols-3 gap-2 text-center text-sm mb-3">
                    <div className="bg-white/50 rounded-lg p-2">
                      <p className="font-bold">{data.disagioAvg}</p>
                      <p className="text-[10px] text-gray-500">Disagio</p>
                    </div>
                    <div className="bg-white/50 rounded-lg p-2">
                      <p className="font-bold">{data.performanceAvg}</p>
                      <p className="text-[10px] text-gray-500">Performance</p>
                    </div>
                    <div className="bg-white/50 rounded-lg p-2">
                      <p className={`font-bold ${data.delta >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {data.delta > 0 ? '+' : ''}{data.delta}
                      </p>
                      <p className="text-[10px] text-gray-500">Δ</p>
                    </div>
                  </div>
                  
                  <p className="text-xs text-gray-600">{data.feedback}</p>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Legend */}
        <Card className="bg-gray-50">
          <CardContent className="p-4">
            <h4 className="font-semibold text-sm mb-2">Come leggere i risultati</h4>
            <ul className="text-xs text-gray-600 space-y-1">
              <li><strong>Delta (Δ) = Performance - Disagio</strong></li>
              <li>• Δ positivo → Buona integrazione (agisci più di quanto ti costa)</li>
              <li>• |Δ| ≤ 1 → Zona neutra (equilibrio)</li>
              <li>• Δ negativo → Fatica interna (ti costa più di quanto agisci)</li>
              <li>• Δ molto negativo (&lt; -2) → Area critica</li>
            </ul>
          </CardContent>
        </Card>

        <div className="flex gap-3">
          <Button variant="outline" onClick={resetSibTest} className="flex-1 rounded-xl">
            Rifai il Test
          </Button>
          <Button 
            onClick={goBackToHub} 
            className="flex-1 rounded-xl bg-gradient-to-r from-indigo-600 to-purple-600"
          >
            Torna ai Test
          </Button>
        </div>
      </div>
    );
  }

  return null;
};

export default AssertivitaPage;
